import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { useAuth } from "./hooks/useAuth";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RedirecionarPerfisAdministrativos } from "./pages/Admin/PerfisAdministrativos";

// Componentes de autenticação (carregados imediatamente)
import LoginPage from "./components/Auth/LoginPage";
import RegisterPage from "./components/Auth/RegisterPage";
import RegisterSuccessPage from "./components/Auth/RegisterSuccessPage";
import EsqueciSenhaPage from "./components/Auth/EsqueciSenhaPage";

// Lazy loading para componentes autenticados (carregados sob demanda)
// Proponente
const HomeProponente = lazy(() => import("./components/Proponente/Home/HomeProponente"));
const MeuCadastroPage = lazy(() => import("./components/Proponente/MeuCadastro/MeuCadastroPage"));
const CompletarCadastroWizard = lazy(() => import("./components/Proponente/CompletarCadastro/CompletarCadastroWizard"));
const ListaEditaisPublicos = lazy(() => import("./components/Proponente/Editais").then(m => ({ default: m.ListaEditaisPublicos })));
const DetalhesEditalPublico = lazy(() => import("./components/Proponente/Editais").then(m => ({ default: m.DetalhesEditalPublico })));
const MinhasInscricoes = lazy(() => import("./components/Proponente/Inscricoes").then(m => ({ default: m.MinhasInscricoes })));
const FormInscricao = lazy(() => import("./components/Proponente/Inscricoes").then(m => ({ default: m.FormInscricao })));
const DetalhesInscricaoProponente = lazy(() => import("./components/Proponente/Inscricoes").then(m => ({ default: m.DetalhesInscricao })));
const ResultadoInscricao = lazy(() => import("./pages/Proponente/Resultado/ResultadoInscricao"));
const MinhasAreasExpertise = lazy(() => import("./pages/Proponente/MinhasAreasExpertise").then(m => ({ default: m.MinhasAreasExpertise })));
const ConviteEquipe = lazy(() => import("./pages/ConviteEquipe"));

// Admin
const HomeAdmin = lazy(() => import("./components/Admin/Home/Home"));
const RepositorioHistorico = lazy(() => import("./pages/Admin/RepositorioHistorico"));
const RelatoriosBI = lazy(() => import("./pages/Admin/RelatoriosBI"));
const AdminAuditoria = lazy(() => import("./pages/Admin/Auditoria"));
const AdminInstituicoes = lazy(() => import("./pages/Admin/Instituicoes"));
const AdminVinculosEspecificos = lazy(() => import("./pages/Admin/VinculosEspecificos"));
const AdminCoordenacoes = lazy(() => import("./pages/Admin/Coordenacoes"));
const PagamentosPorEdital = lazy(() => import("./pages/Admin/PagamentosPorEdital"));
const PagamentosDoEditalDetalhe = lazy(() => import("./pages/Admin/PagamentosDoEditalDetalhe"));
const Proponentes = lazy(() => import("./components/Admin/Proponentes/Proponentes"));
const ProponenteDetail = lazy(() => import("./components/Admin/Proponentes/ProponenteDetail"));
const AdminAvaliadores = lazy(() => import("./pages/Admin/Avaliadores/AdminAvaliadores").then(m => ({ default: m.AdminAvaliadores })));
const AdminUsuarios = lazy(() => import("./pages/Admin/Usuarios/AdminUsuarios"));
const ListaInscricoes = lazy(() => import("./components/Admin/Inscricoes").then(m => ({ default: m.ListaInscricoes })));
const DetalhesInscricao = lazy(() => import("./components/Admin/Inscricoes").then(m => ({ default: m.DetalhesInscricao })));
const TriagemInscricao = lazy(() => import("./components/Admin/Inscricoes/TriagemInscricao").then(m => ({ default: m.TriagemInscricao })));

// Contratos (Termos de Outorga)
const Contratos = lazy(() => import("./components/Admin/Contratos").then(m => ({ default: m.Contratos })));
const FormContrato = lazy(() => import("./components/Admin/Contratos").then(m => ({ default: m.FormContrato })));
const DetalhesContrato = lazy(() => import("./components/Admin/Contratos").then(m => ({ default: m.DetalhesContrato })));

const ListaEditais = lazy(() => import("./components/Admin/Editais").then(m => ({ default: m.ListaEditais })));
const FormEdital = lazy(() => import("./components/Admin/Editais").then(m => ({ default: m.FormEdital })));
const DetalhesEdital = lazy(() => import("./components/Admin/Editais").then(m => ({ default: m.DetalhesEdital })));
const GerenciarAditivos = lazy(() => import("./components/Admin/Editais/GerenciarAditivos"));
const RelatorioEdital = lazy(() => import("./pages/Admin/Editais/RelatorioEdital"));
const ListaTemplates = lazy(() => import("./components/Admin/Templates").then(m => ({ default: m.ListaTemplates })));
const FormTemplate = lazy(() => import("./components/Admin/Templates").then(m => ({ default: m.FormTemplate })));
const DetalhesTemplate = lazy(() => import("./components/Admin/Templates").then(m => ({ default: m.DetalhesTemplate })));
const GestaoCriterios = lazy(() => import("./components/Admin/Criterios/GestaoCriterios"));
const ConviteAvaliadores = lazy(() => import("./components/Admin/Avaliacoes/ConviteAvaliadores"));
const DashboardAvaliacoes = lazy(() => import("./pages/Admin/Avaliacoes/DashboardAvaliacoes"));
const MonitorarAvaliacoes = lazy(() => import("./pages/Admin/Avaliacoes/MonitorarAvaliacoes"));

// Recursos (Proponente)
const CriarRecurso = lazy(() => import("./components/Proponente/Recursos/CriarRecurso"));
const MeusRecursos = lazy(() => import("./components/Proponente/Recursos/MeusRecursos"));
const DetalhesRecurso = lazy(() => import("./components/Proponente/Recursos/DetalhesRecurso"));

// Recursos (Admin)
const RecursosDashboard = lazy(() => import("./pages/Admin/Recursos/RecursosDashboard"));
const AnalisarRecurso = lazy(() => import("./pages/Admin/Recursos/AnalisarRecurso"));
const RelatorioRecursos = lazy(() => import("./pages/Admin/Recursos/RelatorioRecursos"));

// Prestação de Contas (Proponente)
const MeusContratos = lazy(() => import("./pages/Proponente/PrestacaoContas/MeusContratos"));
const Acompanhamento = lazy(() => import("./pages/Proponente/Acompanhamento/Acompanhamento"));
const MeuContrato = lazy(() => import("./pages/Proponente/PrestacaoContas/MeuContrato"));
const MeusRelatorios = lazy(() => import("./pages/Proponente/PrestacaoContas/MeusRelatorios"));
const NovoRelatorio = lazy(() => import("./pages/Proponente/PrestacaoContas/NovoRelatorio"));
const DetalhesRelatorio = lazy(() => import("./pages/Proponente/PrestacaoContas/DetalhesRelatorio"));

// Prestação de Contas (Admin)
const PrestacaoContasList = lazy(() => import("./pages/Admin/PrestacaoContas/PrestacaoContasList"));
const PrestacaoContasDetalhe = lazy(() => import("./pages/Admin/PrestacaoContas/PrestacaoContasDetalhe"));

// Tomada de Contas Especial (Admin)
const TcePainel = lazy(() => import("./pages/Admin/Tce/TcePainel"));
const TceNovaProposta = lazy(() => import("./pages/Admin/Tce/TceNovaProposta"));
const TceProcessoDetalhes = lazy(() => import("./pages/Admin/Tce/TceProcessoDetalhes"));
const MeusConvites = lazy(() => import("./pages/Avaliador/MeusConvites"));
const MinhasAvaliacoes = lazy(() => import("./pages/Avaliador/MinhasAvaliacoes"));
const AvaliarProjeto = lazy(() => import("./pages/Avaliador/AvaliarProjeto"));

// Componente de loading para lazy imports
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-600">Carregando...</p>
    </div>
  </div>
);

// Componente de rotas que usa o contexto de auth
function AppRoutes() {
  const { user, isAuthenticated, loading } = useAuth();

  // Enquanto está verificando autenticação, faz verificação silenciosa
  if (loading) {
    return null;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
      {/* Rota inicial com redirecionamento inteligente */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            user?.tipo_usuario === "proponente" &&
            user.has_proponente === false ? (
              <Navigate to="/completar-cadastro" replace />
            ) : (
              <Navigate
                to={user?.tipo_usuario === "admin" ? "/admin/home" : "/home"}
                replace
              />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Rotas públicas (sem autenticação) */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            user?.tipo_usuario === "proponente" &&
            user.has_proponente === false ? (
              <Navigate to="/completar-cadastro" replace />
            ) : (
              <Navigate
                to={user?.tipo_usuario === "admin" ? "/admin/home" : "/home"}
                replace
              />
            )
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/cadastro"
        element={
          isAuthenticated ? (
            <Navigate
              to={user?.tipo_usuario === "admin" ? "/admin/home" : "/home"}
              replace
            />
          ) : (
            <RegisterPage />
          )
        }
      />
      <Route path="/register/success" element={<RegisterSuccessPage />} />
      <Route path="/esqueci-senha" element={<EsqueciSenhaPage />} />
      <Route path="/convites/equipe/:token" element={<ConviteEquipe />} />

      {/* Rotas protegidas - Usuário */}
      <Route
        path="/home"
        element={
          <ProtectedRoute requiredRole="proponente">
            <HomeProponente />
          </ProtectedRoute>
        }
      />

      <Route
        path="/meu-cadastro"
        element={
          <ProtectedRoute requiredRole="proponente">
            <MeuCadastroPage />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Proponente (editais) */}
      <Route
        path="/editais"
        element={
          <ProtectedRoute requiredRole="proponente">
            <ListaEditaisPublicos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/editais/:id"
        element={
          <ProtectedRoute requiredRole="proponente">
            <DetalhesEditalPublico />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Proponente (inscrições) */}
      <Route
        path="/inscricoes"
        element={
          <ProtectedRoute requiredRole="proponente">
            <MinhasInscricoes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inscricoes/nova"
        element={
          <ProtectedRoute requiredRole="proponente">
            <FormInscricao />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inscricoes/:id"
        element={
          <ProtectedRoute requiredRole="proponente">
            <DetalhesInscricaoProponente />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inscricoes/:id/resultado"
        element={
          <ProtectedRoute requiredRole="proponente">
            <ResultadoInscricao />
          </ProtectedRoute>
        }
      />
      <Route
        path="/areas-expertise"
        element={
          <ProtectedRoute requiredRole="proponente">
            <MinhasAreasExpertise />
          </ProtectedRoute>
        }
      />

      {/* Rota para completar cadastro - Proponente */}
      <Route
        path="/completar-cadastro"
        element={
          <ProtectedRoute requiredRole="proponente">
            <CompletarCadastroWizard />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Proponente (recursos) */}
      <Route
        path="/inscricoes/:inscricaoId/recurso/novo"
        element={
          <ProtectedRoute requiredRole="proponente">
            <CriarRecurso />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meus-recursos"
        element={
          <ProtectedRoute requiredRole="proponente">
            <MeusRecursos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meus-recursos/:id"
        element={
          <ProtectedRoute requiredRole="proponente">
            <DetalhesRecurso />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (proponentes) */}
      <Route
        path="/admin/proponentes"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="proponentes">
            <Proponentes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/proponentes/:id"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="proponentes">
            <ProponenteDetail />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (avaliadores) */}
      <Route
        path="/admin/avaliadores"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="avaliadores">
            <AdminAvaliadores />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin */}
      <Route
        path="/admin/usuarios"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="usuarios_admin">
            <AdminUsuarios />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/home"
        element={
          <ProtectedRoute requiredRole="admin">
            <HomeAdmin />
          </ProtectedRoute>
        }
      />
      <Route path="/admin/repositorio-historico" element={<ProtectedRoute requiredRole="admin" requiredModulo="repositorio_historico"><RepositorioHistorico /></ProtectedRoute>} />
      <Route path="/admin/relatorios-bi" element={<ProtectedRoute requiredRole="admin" requiredModulo="relatorios"><RelatoriosBI /></ProtectedRoute>} />
      <Route path="/admin/auditoria" element={<ProtectedRoute requiredRole="admin" requiredModulo="auditoria"><AdminAuditoria /></ProtectedRoute>} />
      <Route path="/admin/instituicoes" element={<ProtectedRoute requiredRole="admin" requiredModulo="instituicoes"><AdminInstituicoes /></ProtectedRoute>} />
      <Route path="/admin/vinculos-especificos" element={<ProtectedRoute requiredRole="admin" requiredModulo="instituicoes"><AdminVinculosEspecificos /></ProtectedRoute>} />
      <Route path="/admin/perfis" element={<ProtectedRoute requiredRole="admin" requiredModulo="usuarios_admin"><RedirecionarPerfisAdministrativos /></ProtectedRoute>} />
      <Route path="/admin/coordenacoes" element={<ProtectedRoute requiredRole="admin" requiredModulo="coordenacoes"><AdminCoordenacoes /></ProtectedRoute>} />

      {/* Rotas protegidas - Admin (templates) */}
      <Route
        path="/admin/templates"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="templates">
            <ListaTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/templates/novo"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="templates">
            <FormTemplate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/templates/:id"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="templates">
            <DetalhesTemplate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/templates/:id/editar"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="templates">
            <FormTemplate />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (editais) */}
      <Route
        path="/admin/editais"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="editais">
            <ListaEditais />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/editais/novo"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="editais">
            <FormEdital />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/editais/:id"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="editais">
            <DetalhesEdital />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/editais/:id/editar"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="editais">
            <FormEdital />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/editais/:id/aditivos"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="editais">
            <GerenciarAditivos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/editais/:id/relatorio"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo={["editais", "avaliacao"]}>
            <RelatorioEdital />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (inscrições) */}
      <Route
        path="/admin/inscricoes"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="inscricoes">
            <ListaInscricoes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inscricoes/:id"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="inscricoes">
            <DetalhesInscricao />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inscricoes/:id/triagem"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="inscricoes">
            <TriagemInscricao />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (Contratos - Termos de Outorga) */}
      <Route
        path="/admin/contratos"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="contratos">
            <Contratos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/contratos/novo"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="contratos">
            <FormContrato />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/contratos/:id"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="contratos">
            <DetalhesContrato />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (Financeiro: pagamentos agrupados por edital) */}
      <Route
        path="/admin/financeiro/pagamentos-por-edital"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="financeiro">
            <PagamentosPorEdital />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/financeiro/pagamentos-por-edital/:editalId"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="financeiro">
            <PagamentosDoEditalDetalhe />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (critérios de avaliação) */}
      <Route
        path="/admin/editais/:id/criterios"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="avaliacao">
            <GestaoCriterios />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (avaliações) */}
      <Route
        path="/admin/inscricoes/:id/avaliacoes"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="avaliacao">
            <ConviteAvaliadores />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/avaliacoes/dashboard"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="avaliacao">
            <DashboardAvaliacoes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projetos/:id/avaliacoes"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="avaliacao">
            <MonitorarAvaliacoes />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (recursos) */}
      <Route
        path="/admin/editais/:editalId/recursos"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="avaliacao">
            <RecursosDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/editais/:editalId/recursos/relatorio"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="avaliacao">
            <RelatorioRecursos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/recursos/:id"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="avaliacao">
            <AnalisarRecurso />
          </ProtectedRoute>
        }
      />

      {/* Acompanhamento da execução (proponente) */}
      <Route
        path="/proponente/acompanhamento"
        element={
          <ProtectedRoute requiredRole="proponente">
            <Acompanhamento />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Prestação de Contas (proponente) */}
      <Route
        path="/proponente/prestacao/contratos"
        element={
          <ProtectedRoute requiredRole="proponente">
            <MeusContratos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proponente/prestacao/contratos/:id"
        element={
          <ProtectedRoute requiredRole="proponente">
            <MeuContrato />
          </ProtectedRoute>
        }
      />
      {/* Redirect da rota antiga */}
      <Route
        path="/proponente/prestacao/contrato"
        element={<Navigate to="/proponente/prestacao/contratos" replace />}
      />
      <Route
        path="/proponente/prestacao/relatorios"
        element={
          <ProtectedRoute requiredRole="proponente">
            <MeusRelatorios />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proponente/prestacao/relatorios/novo"
        element={
          <ProtectedRoute requiredRole="proponente">
            <NovoRelatorio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proponente/prestacao/relatorios/:id"
        element={
          <ProtectedRoute requiredRole="proponente">
            <DetalhesRelatorio />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Prestação de Contas (admin) */}
      <Route
        path="/admin/prestacao"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="prestacao_contas">
            <PrestacaoContasList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/prestacao/:id"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="prestacao_contas">
            <PrestacaoContasDetalhe />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Tomada de Contas Especial (TCE) */}
      <Route
        path="/admin/tce"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="tce">
            <TcePainel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tce/proposta/nova"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="tce">
            <TceNovaProposta />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tce/:id"
        element={
          <ProtectedRoute requiredRole="admin" requiredModulo="tce">
            <TceProcessoDetalhes />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Avaliador */}
      <Route
        path="/avaliador/convites"
        element={
          <ProtectedRoute>
            <MeusConvites />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avaliador/avaliacoes"
        element={
          <ProtectedRoute>
            <MinhasAvaliacoes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avaliador/avaliar/:id"
        element={
          <ProtectedRoute>
            <AvaliarProjeto />
          </ProtectedRoute>
        }
      />

      {/* Rota 404 - redireciona baseado na autenticação */}
      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />}
      />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
